import {
  ACCEPTANCE_METHODS,
  type BasePriceSource,
  type CatalogItemDto,
  datePl,
  computeVariant,
  type Discount,
  looksLikeDevice,
  type MeDto,
  type OfferDto,
  type OfferVersionDto,
  PRESENTATIONS,
  type Presentation,
  type ScopeDto,
  VAT_PROFILES,
  serviceVatWarning,
  missingCostNote,
  marginIncompleteNote,
  unitGrossGr,
  unitNetFromGrossGr,
  VAT_RATES,
  type VatProfile,
} from '@bqcrm/shared';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Grid,
  Group,
  Menu,
  Modal,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconCopy,
  IconDots,
  IconFileTypePdf,
  IconPlus,
  IconRefresh,
  IconSend,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../../api';
import { can, useMe } from '../../auth';
import { CompanyBadgeById, TradeBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard, UnsavedHint } from '../../components/ui';
import { Money, MoneyInput } from '../../components/Money';
import { QueryState } from '../../components/QueryState';
import { useCase, useTrades } from '../../data';
import { formatDateTime, pctPl, qtyPl } from '../../format';
import { notifyError, notifyOk, notifyWarn } from '../../notify';
import { IssueWarnings } from '../../components/IssueWarnings';
import { CatalogPicker, ShareDialog, VersionStatusBadge } from './offer-ui';
import { type DecisionDialog, OfferDecisionInfo, OfferDecisionModals } from './OfferDecisions';
import { ContractTermsCard } from './ContractTermsCard';
import { ask } from '../../confirm';
import { useSaveShortcut } from '../../hooks';
import { minHint } from '../../minHint';
import { IssueRequestInfo } from './IssueRequestInfo';

interface DraftItem {
  key: string;
  id: string | null;
  scopeId: string;
  catalogItemId: string | null;
  kind: string;
  name: string;
  description: string;
  unit: string;
  quantity: number;
  unitPriceGr: number;
  listPriceGr: number;
  currentCatalogPriceGr: number | null;
  currentCatalogPurchaseGr: number | null;
  discount: Discount | null;
  vatRate: number;
  purchasePriceGr: number | null;
  /** źródło i data ceny zakupu w kalkulacji (z serwera; nowa pozycja — po zapisie) */
  purchasePriceSource?: BasePriceSource | null;
  purchasePriceDate?: string | null;
  purchasePriceStale?: boolean;
  components: { name: string; quantity: number; unit: string }[];
  refreshFromCatalog: boolean;
}
interface DraftVariant {
  key: string;
  name: string;
  description: string;
  presentation: Presentation;
  discount: Discount | null;
  items: DraftItem[];
}
interface Draft {
  title: string;
  validUntil: string;
  terms: string;
  changeNote: string;
  vatProfile: VatProfile;
  variants: DraftVariant[];
}

let seq = 0;
const k = () => `k${++seq}`;

function toDraft(v: OfferVersionDto): Draft {
  return {
    title: v.title,
    validUntil: v.validUntil,
    terms: v.terms,
    changeNote: v.changeNote,
    vatProfile: v.vatProfile,
    variants: v.variants.map((va) => ({
      key: k(),
      name: va.name,
      description: va.description,
      presentation: va.presentation,
      discount: va.discount,
      items: va.items.map((i) => ({ ...i, key: k(), refreshFromCatalog: false })),
    })),
  };
}

const toPayload = (d: Draft) => ({
  ...d,
  variants: d.variants.map(({ key: _k, items, ...v }) => ({
    ...v,
    items: items.map(({ key: _i, kind: _kd, listPriceGr: _l, currentCatalogPriceGr: _c, currentCatalogPurchaseGr: _cp, ...i }) => i),
  })),
});

const totalsOf = (v: DraftVariant) =>
  computeVariant(
    v.items.map((i) => ({ quantity: i.quantity, unitPriceGr: i.unitPriceGr, listPriceGr: i.listPriceGr, discount: i.discount, vatRate: i.vatRate, purchasePriceGr: null })),
    v.discount,
  );

function DiscountInput({ value, onChange, disabled }: { value: Discount | null; onChange: (d: Discount | null) => void; disabled?: boolean }) {
  // rodzaj rabatu pamiętany lokalnie, żeby przełączenie nie wymagało od razu wpisania wartości
  const [type, setType] = useState<Discount['type']>(value?.type ?? 'procent');
  return (
    <Group gap={2} wrap="nowrap">
      {type === 'procent' ? (
        <NumberInput
          size="xs"
          w={64}
          min={0}
          max={100}
          decimalScale={2}
          hideControls
          disabled={disabled}
          value={value?.type === 'procent' ? value.value : ''}
          onChange={(v) => onChange(v === '' || Number(v) === 0 ? null : { type: 'procent', value: Number(v) })}
        />
      ) : (
        <MoneyInput size="xs" w={90} disabled={disabled} valueGr={value?.type === 'kwota' ? value.value : null} onChangeGr={(gr) => onChange(gr ? { type: 'kwota', value: gr } : null)} />
      )}
      <ActionIcon
        size="sm"
        variant="subtle"
        disabled={disabled}
        onClick={() => {
          setType(type === 'procent' ? 'kwota' : 'procent');
          onChange(null);
        }}
        aria-label="Zmień rodzaj rabatu (procent / kwota)"
      >
        <Text size="xs" fw={700}>
          {type === 'procent' ? '%' : 'zł'}
        </Text>
      </ActionIcon>
    </Group>
  );
}

/** Pozycje z katalogu, których cena sprzedaży (albo zakupu) w cenniku różni się od tej zapamiętanej w ofercie. */
function staleItems(v: DraftVariant): DraftItem[] {
  return v.items.filter(
    (i) =>
      i.catalogItemId &&
      !i.refreshFromCatalog &&
      ((i.currentCatalogPriceGr !== null && i.currentCatalogPriceGr !== i.listPriceGr) || (i.currentCatalogPurchaseGr !== null && i.purchasePriceGr !== null && i.currentCatalogPurchaseGr !== i.purchasePriceGr)),
  );
}

const lineNet = (i: Pick<DraftItem, 'quantity' | 'unitPriceGr' | 'discount' | 'vatRate'>) =>
  computeVariant([{ quantity: i.quantity, unitPriceGr: i.unitPriceGr, listPriceGr: i.unitPriceGr, discount: i.discount, vatRate: i.vatRate, purchasePriceGr: null }], null).netGr;
const pctOrDash = (v: number | null) => (v === null ? '—' : pctPl(v));

/** Zbiorcze odświeżenie cen wariantu z katalogu — z podglądem zmiany wartości i marży przed zapisem. */
const COST_SOURCES: Record<BasePriceSource, string> = { zakup: 'ostatni zakup', reczna: 'cena bazowa', oferta: 'wpisana w ofercie' };

/**
 * Cena zakupu w kalkulacji pozycji (tylko z uprawnieniem ceny.zakup): źródło i data, ostrzeżenie o nieaktualnej cenie.
 * Produkt bez ceny bazowej i bez zakupów — cenę wpisuje handlowiec; zapis na kartę produktu to osobna, świadoma operacja.
 */
function PurchaseCost({ item: i, editable, onChange }: { item: DraftItem; editable: boolean; onChange: (gr: number | null) => void }) {
  const me = useMe().data;
  const qc = useQueryClient();
  const seeCost = !!me && can(me, 'ceny.zakup');
  const canSaveBase = !!me && (can(me, 'katalog.cena_bazowa') || can(me, 'katalog.edycja'));
  const saveBase = useMutation({
    mutationFn: () => api(`/catalog/${i.catalogItemId}/base-price`, { method: 'POST', body: { purchasePriceGr: i.purchasePriceGr, source: 'oferta', note: `z oferty: ${i.name}` } }),
    onSuccess: () => {
      notifyOk('Zapisano cenę bazową na karcie produktu');
      void qc.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: notifyError,
  });
  // część modułu magazynu (ceny bazowe, źródło i aktualność ceny) — bez modułu oferta wygląda jak przed nim
  if (!seeCost || !me?.features.magazyn) return null;
  // nowa pozycja z katalogu z ceną bazową — źródło znane po zapisie
  const typed = i.purchasePriceSource === 'oferta' || (i.catalogItemId !== null && i.id === null && i.currentCatalogPurchaseGr === null) || (i.catalogItemId !== null && i.purchasePriceGr === null && i.id !== null);
  if (editable && (typed || i.kind === 'wlasna')) {
    return (
      <Group gap={4} wrap="nowrap">
        <Text size="xs" c="dimmed">
          zakup:
        </Text>
        <MoneyInput size="xs" w={110} valueGr={i.purchasePriceGr} onChangeGr={onChange} placeholder="cena zakupu" aria-label="Cena zakupu netto" />
        {i.catalogItemId && i.purchasePriceGr !== null && canSaveBase && (
          <Button size="compact-xs" variant="subtle" loading={saveBase.isPending} onClick={() => saveBase.mutate()}>
            Zapisz jako cenę bazową
          </Button>
        )}
      </Group>
    );
  }
  if (i.purchasePriceGr === null && i.id === null && i.currentCatalogPurchaseGr !== null) {
    return (
      <Badge size="xs" color="gray" variant="light">
        zakup <Money gr={i.currentCatalogPurchaseGr} />
      </Badge>
    );
  }
  if (i.purchasePriceGr === null) return null;
  return (
    <Tooltip label={i.purchasePriceStale ? 'Cena zakupu starsza niż ustawiony próg — sprawdź u dostawcy' : 'Cena zakupu przyjęta do kalkulacji (migawka)'}>
      <Badge size="xs" color={i.purchasePriceStale ? 'orange' : 'gray'} variant="light">
        zakup <Money gr={i.purchasePriceGr} />
        {i.purchasePriceSource ? ` · ${COST_SOURCES[i.purchasePriceSource]}` : ''}
        {i.purchasePriceDate ? ` · ${datePl(i.purchasePriceDate)}` : ''}
        {i.purchasePriceStale ? ' · nieaktualna' : ''}
      </Badge>
    </Tooltip>
  );
}

function PriceRefreshModal({ v, opened, onClose, onApply, seeMargin }: { v: DraftVariant; opened: boolean; onClose: () => void; onApply: (keys: string[]) => void; seeMargin: boolean }) {
  const stale = staleItems(v);
  // pozycje z ręcznie zmienioną ceną domyślnie pomijamy — mogły być negocjowane
  const [picked, setPicked] = useState<string[]>([]);
  useEffect(() => {
    if (opened) setPicked(stale.filter((i) => i.unitPriceGr === i.listPriceGr).map((i) => i.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);
  const after = (i: DraftItem): DraftItem =>
    picked.includes(i.key)
      ? { ...i, unitPriceGr: i.currentCatalogPriceGr ?? i.unitPriceGr, listPriceGr: i.currentCatalogPriceGr ?? i.listPriceGr, purchasePriceGr: i.currentCatalogPurchaseGr ?? i.purchasePriceGr }
      : i;
  const calc = (items: DraftItem[]) =>
    computeVariant(
      items.map((i) => ({ quantity: i.quantity, unitPriceGr: i.unitPriceGr, listPriceGr: i.listPriceGr, discount: i.discount, vatRate: i.vatRate, purchasePriceGr: i.purchasePriceGr })),
      v.discount,
    );
  const before = calc(v.items);
  const next = calc(v.items.map(after));
  const all = stale.length > 0 && picked.length === stale.length;
  return (
    <Modal opened={opened} onClose={onClose} title={`Aktualne ceny z katalogu — ${v.name}`} size="xl">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Ceny w ofercie są zapamiętane z chwili dodania pozycji. Zaznacz pozycje, które mają dostać ceny z obecnego cennika. Rabaty pozycji zostają. Pozycje z ręcznie zmienioną ceną są domyślnie pominięte.
        </Text>
        <Table.ScrollContainer minWidth={seeMargin ? 760 : 620}>
          <Table verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={32}>
                  <Checkbox aria-label="Zaznacz wszystkie" checked={all} indeterminate={!all && picked.length > 0} onChange={() => setPicked(all ? [] : stale.map((i) => i.key))} />
                </Table.Th>
                <Table.Th>Pozycja</Table.Th>
                <Table.Th ta="right">Cena w ofercie</Table.Th>
                <Table.Th ta="right">W katalogu teraz</Table.Th>
                <Table.Th ta="right">Zmiana wartości</Table.Th>
                {seeMargin && <Table.Th ta="right">Zakup: oferta → katalog</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {stale.map((i) => {
                const priceNow = i.currentCatalogPriceGr ?? i.unitPriceGr;
                // po rabacie pozycji (rabat na cały wariant jest w sumie poniżej)
                const delta = lineNet({ ...i, unitPriceGr: priceNow }) - lineNet(i);
                return (
                  <Table.Tr key={i.key}>
                    <Table.Td>
                      <Checkbox aria-label={`Aktualizuj ${i.name}`} checked={picked.includes(i.key)} onChange={(e) => setPicked(e.currentTarget.checked ? [...picked, i.key] : picked.filter((x) => x !== i.key))} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{i.name}</Text>
                      <Text size="xs" c="dimmed">
                        {qtyPl(i.quantity)} {i.unit}
                        {i.unitPriceGr !== i.listPriceGr ? ' · cena zmieniona ręcznie w ofercie' : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Money gr={i.unitPriceGr} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <Money gr={priceNow} />
                    </Table.Td>
                    <Table.Td ta="right" c={delta > 0 ? 'var(--app-text-eko)' : delta < 0 ? 'var(--app-text-red)' : undefined}>
                      {delta > 0 ? '+' : ''}
                      <Money gr={delta} />
                    </Table.Td>
                    {seeMargin && (
                      <Table.Td ta="right">
                        <Text size="xs" span>
                          <Money gr={i.purchasePriceGr} /> → <Money gr={i.currentCatalogPurchaseGr ?? i.purchasePriceGr} />
                        </Text>
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Paper withBorder p="sm" radius="md">
          <Group gap="xl" wrap="wrap">
            <div>
              <Text size="xs" c="dimmed">
                Wartość wariantu netto
              </Text>
              <Text size="sm" fw={600} className="tabular">
                <Money gr={before.netGr} /> → <Money gr={next.netGr} />
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Brutto
              </Text>
              <Text size="sm" fw={600} className="tabular">
                <Money gr={before.grossGr} /> → <Money gr={next.grossGr} />
              </Text>
            </div>
            {seeMargin && (
              <div>
                <Text size="xs" c="dimmed">
                  Marża
                </Text>
                <Text size="sm" fw={600} className="tabular">
                  {pctOrDash(before.marginPct)} → {pctOrDash(next.marginPct)}
                </Text>
              </div>
            )}
          </Group>
        </Paper>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button leftSection={<IconRefresh size={16} />} disabled={!picked.length} onClick={() => onApply(picked)}>
            Zaktualizuj zaznaczone ({picked.length})
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function VariantEditor({
  v,
  editable,
  scopes,
  tradeName,
  onChange,
  seeMargin,
  seeCost = false,
  serverTotals,
  mineScopes = null,
  headerEditable = true,
  preparerOf,
  profileRate = null,
}: {
  v: DraftVariant;
  editable: boolean;
  /** oferta wspólna: zakresy, których pozycje zmieniam (null — wszystkie) */
  mineScopes?: string[] | null;
  /** nazwa, opis, prezentacja, rabat wariantu — w ofercie wspólnej tylko autor */
  headerEditable?: boolean;
  /** kto przygotowuje pozycje zakresu (oferta wspólna) */
  preparerOf?: (scopeId: string) => string | null;
  /** stawka z profilu VAT oferty (8 / 23) dla nowych pozycji; null — stawka z katalogu (QA P6) */
  profileRate?: number | null;
  scopes: ScopeDto[];
  tradeName: (code: string) => string;
  onChange: (v: DraftVariant) => void;
  seeMargin: boolean;
  /** QA8 P1: uprawnienie do cen zakupu — kolumna „Koszt zakupu” (pozycje własne do wpisania) */
  seeCost?: boolean;
  serverTotals?: OfferVersionDto['variants'][number]['totals'];
}) {
  const [pickerOpen, picker] = useDisclosure();
  const [refreshOpen, refresh] = useDisclosure();
  const stale = editable ? staleItems(v) : [];
  const applyRefresh = (keys: string[]) => {
    onChange({
      ...v,
      items: v.items.map((i) =>
        keys.includes(i.key)
          ? { ...i, refreshFromCatalog: true, unitPriceGr: i.currentCatalogPriceGr ?? i.unitPriceGr, listPriceGr: i.currentCatalogPriceGr ?? i.listPriceGr, purchasePriceGr: i.currentCatalogPurchaseGr ?? i.purchasePriceGr }
          : i,
      ),
    });
    refresh.close();
    notifyOk(`Zaktualizowano ceny: ${keys.length} — zapisz ofertę, aby je zachować`);
  };
  const totals = useMemo(() => totalsOf(v), [v]);
  // oferta wspólna: każdy zmienia tylko pozycje swoich branż
  const own = (scopeId: string) => !mineScopes || mineScopes.includes(scopeId);
  const myScopes = scopes.filter((s) => own(s.id));
  const canAdd = editable && myScopes.length > 0;
  const headerOn = editable && headerEditable;
  const setItem = (key: string, patch: Partial<DraftItem>) => onChange({ ...v, items: v.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) });
  const move = (idx: number, d: -1 | 1) => {
    const items = [...v.items];
    const j = idx + d;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j]!, items[idx]!];
    onChange({ ...v, items });
  };
  const add = (c: CatalogItemDto) => {
    const scope = myScopes.find((s) => c.tradeCodes.includes(s.tradeCode)) ?? myScopes[0]!;
    // cena sprzedaży: ze stałej marży od ceny do kalkulacji (ostatni zakup → ręczna), inaczej z cennika
    const sale = c.calcSalePriceGr ?? c.salePriceGr;
    onChange({
      ...v,
      items: [
        ...v.items,
        {
          key: k(),
          id: null,
          scopeId: scope.id,
          catalogItemId: c.id,
          kind: c.kind,
          name: c.name,
          description: c.description,
          unit: c.unit,
          quantity: 1,
          unitPriceGr: sale ?? 0,
          listPriceGr: sale ?? 0,
          currentCatalogPriceGr: sale,
          currentCatalogPurchaseGr: c.basePrice?.effectiveGr ?? c.purchasePriceGr,
          discount: null,
          vatRate: profileRate ?? c.vatRate,
          purchasePriceGr: null,
          components: c.components.map((x) => ({ name: x.name, quantity: x.quantity, unit: x.unit })),
          refreshFromCatalog: false,
        },
      ],
    });
    picker.close();
  };
  const addCustom = () =>
    onChange({
      ...v,
      items: [
        ...v.items,
        {
          key: k(),
          id: null,
          scopeId: myScopes[0]!.id,
          catalogItemId: null,
          kind: 'wlasna',
          name: 'Pozycja własna',
          description: '',
          unit: 'usł.',
          quantity: 1,
          unitPriceGr: 0,
          listPriceGr: 0,
          currentCatalogPriceGr: null,
          currentCatalogPurchaseGr: null,
          discount: null,
          vatRate: profileRate ?? 8,
          purchasePriceGr: null,
          components: [],
          refreshFromCatalog: false,
        },
      ],
    });

  return (
    <Stack>
      <Group align="flex-end" wrap="wrap">
        <TextInput label="Nazwa wariantu" value={v.name} disabled={!headerOn} onChange={(e) => onChange({ ...v, name: e.currentTarget.value })} w={240} />
        <Select
          label="Prezentacja dla klienta"
          data={Object.entries(PRESENTATIONS).map(([value, label]) => ({ value, label }))}
          value={v.presentation}
          disabled={!headerOn}
          onChange={(p) => p && onChange({ ...v, presentation: p as Presentation })}
          allowDeselect={false}
          w={300}
        />
        <div>
          <Text size="sm" fw={500} mb={2}>
            Rabat na całość
          </Text>
          <DiscountInput value={v.discount} disabled={!headerOn} onChange={(d) => onChange({ ...v, discount: d })} />
        </div>
      </Group>
      <Textarea label="Opis wariantu (widoczny dla klienta)" autosize minRows={1} disabled={!headerOn} value={v.description} onChange={(e) => onChange({ ...v, description: e.currentTarget.value })} />

      {stale.length > 0 && (
        <Alert color="orange" variant="light" icon={<IconRefresh size={18} />} p="xs">
          <Group justify="space-between" gap="xs" wrap="wrap">
            <Text size="sm">
              Ceny w katalogu zmieniły się od dodania {stale.length === 1 ? '1 pozycji' : `${stale.length} pozycji`} tego wariantu.
            </Text>
            <Button size="xs" variant="light" color="orange" onClick={refresh.open}>
              Zaktualizuj ceny…
            </Button>
          </Group>
        </Alert>
      )}
      <PriceRefreshModal v={v} opened={refreshOpen} onClose={refresh.close} onApply={applyRefresh} seeMargin={seeMargin} />

      {/* wydana wersja nie ma pól edycji ani kolumn akcji — mieści się w węższej kolumnie */}
      <Table.ScrollContainer minWidth={(editable ? 1110 : 850) + (seeCost ? 130 : 0)}>
        <Table verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              {editable && <Table.Th w={28} />}
              <Table.Th>Pozycja</Table.Th>
              {scopes.length > 1 && <Table.Th w={130}>Zakres</Table.Th>}
              <Table.Th w={90}>Ilość</Table.Th>
              <Table.Th w={130} ta="right">
                Cena netto
              </Table.Th>
              <Table.Th w={130} ta="right">
                <Tooltip label="Cena jednostkowa brutto przed rabatem. Wpisana tutaj — netto przelicza się według stawki VAT pozycji (oferta liczy od netto, więc brutto może różnić się o grosz)." multiline w={300}>
                  <span>Cena brutto</span>
                </Tooltip>
              </Table.Th>
              <Table.Th w={130}>Rabat</Table.Th>
              <Table.Th w={80}>VAT</Table.Th>
              {seeCost && (
                <Table.Th w={130} ta="right">
                  <Tooltip label="Cena zakupu netto za jednostkę — wewnętrzna, nie trafia do klienta. Pozycje własne wpisujesz tutaj, pozycje z katalogu biorą koszt z cennika." multiline w={280}>
                    <span>Koszt zakupu</span>
                  </Tooltip>
                </Table.Th>
              )}
              <Table.Th w={120} ta="right">
                Wartość netto
              </Table.Th>
              {editable && <Table.Th w={36} />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {v.items.map((i, idx) => {
              const priceChanged = i.catalogItemId && i.currentCatalogPriceGr !== null && i.currentCatalogPriceGr !== i.listPriceGr && !i.refreshFromCatalog;
              const rowEdit = editable && own(i.scopeId);
              const foreignBy = !own(i.scopeId) ? preparerOf?.(i.scopeId) : null;
              return (
                <Table.Tr key={i.key} style={editable && !rowEdit ? { opacity: 0.7 } : undefined}>
                  {editable && (
                    <Table.Td>
                      {rowEdit && (
                      <Stack gap={0}>
                        <ActionIcon size="xs" variant="subtle" color="gray" disabled={idx === 0} onClick={() => move(idx, -1)} aria-label="W górę">
                          <IconArrowUp size={12} />
                        </ActionIcon>
                        <ActionIcon size="xs" variant="subtle" color="gray" disabled={idx === v.items.length - 1} onClick={() => move(idx, 1)} aria-label="W dół">
                          <IconArrowDown size={12} />
                        </ActionIcon>
                      </Stack>
                      )}
                    </Table.Td>
                  )}
                  <Table.Td>
                    {rowEdit ? (
                      <TextInput size="xs" value={i.name} onChange={(e) => setItem(i.key, { name: e.currentTarget.value })} />
                    ) : (
                      <Text size="sm">{i.name}</Text>
                    )}
                    <Group gap={4} mt={2}>
                      {foreignBy && (
                        <Badge size="xs" color="blue" variant="light">
                          przygotowuje: {foreignBy}
                        </Badge>
                      )}
                      {i.kind === 'pakiet' && (
                        <Badge size="xs" color="grape" variant="light">
                          pakiet: {i.components.map((c) => c.name).join(', ')}
                        </Badge>
                      )}
                      {i.kind === 'wlasna' && (
                        <Badge size="xs" color="gray" variant="light">
                          pozycja własna
                        </Badge>
                      )}
                      {/* P20: pozycja własna, której nazwa wskazuje urządzenie — w umowie dostanie oznaczenie „urządzenie” */}
                      {i.kind === 'wlasna' && !i.catalogItemId && looksLikeDevice(i.name) && (
                        <Tooltip label="Nazwa wskazuje urządzenie z numerem seryjnym — w umowie pozycja dostanie oznaczenie „urządzenie” (lista protokołu odbioru, karta instalacji). Można je tam zmienić.">
                          <Badge size="xs" color="teal" variant="light">
                            urządzenie
                          </Badge>
                        </Tooltip>
                      )}
                      <PurchaseCost item={i} editable={editable} onChange={(gr) => setItem(i.key, { purchasePriceGr: gr })} />
                      {i.unitPriceGr !== i.listPriceGr && i.catalogItemId && (
                        <Tooltip label="Cena w ofercie różni się od ceny katalogowej z chwili dodania (cennik nie jest zmieniany)">
                          <Badge size="xs" color="yellow" variant="light">
                            cena zmieniona (katalog <Money gr={i.listPriceGr} />)
                          </Badge>
                        </Tooltip>
                      )}
                      {priceChanged && rowEdit && (
                        <Badge
                          size="xs"
                          color="orange"
                          variant="light"
                          style={{ cursor: 'pointer' }}
                          leftSection={<IconRefresh size={10} />}
                          onClick={() => setItem(i.key, { refreshFromCatalog: true, unitPriceGr: i.currentCatalogPriceGr!, listPriceGr: i.currentCatalogPriceGr!, purchasePriceGr: i.currentCatalogPurchaseGr ?? i.purchasePriceGr })}
                        >
                          cennik teraz: <Money gr={i.currentCatalogPriceGr} /> — aktualizuj
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  {scopes.length > 1 && (
                    <Table.Td>
                      <Select
                        size="xs"
                        disabled={!rowEdit}
                        data={(rowEdit ? myScopes : scopes).map((s) => ({ value: s.id, label: tradeName(s.tradeCode) }))}
                        value={i.scopeId}
                        onChange={(s) => s && setItem(i.key, { scopeId: s })}
                        allowDeselect={false}
                      />
                    </Table.Td>
                  )}
                  <Table.Td>
                    <Group gap={2} wrap="nowrap">
                      <NumberInput size="xs" w={64} min={0.001} decimalScale={3} hideControls disabled={!rowEdit} value={i.quantity} onChange={(q) => setItem(i.key, { quantity: Number(q) || 0 })} />
                      <Text size="xs" c="dimmed">
                        {i.unit}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <MoneyInput size="xs" disabled={!rowEdit} aria-label={`Cena netto: ${i.name}`} valueGr={i.unitPriceGr} onChangeGr={(gr) => setItem(i.key, { unitPriceGr: gr ?? 0, ...(i.kind === 'wlasna' ? { listPriceGr: gr ?? 0 } : {}) })} />
                  </Table.Td>
                  <Table.Td>
                    <MoneyInput
                      size="xs"
                      disabled={!rowEdit}
                      aria-label={`Cena brutto: ${i.name}`}
                      valueGr={unitGrossGr(i.unitPriceGr, i.vatRate)}
                      onChangeGr={(gr) => {
                        const net = unitNetFromGrossGr(gr ?? 0, i.vatRate);
                        setItem(i.key, { unitPriceGr: net, ...(i.kind === 'wlasna' ? { listPriceGr: net } : {}) });
                      }}
                    />
                  </Table.Td>
                  <Table.Td>
                    <DiscountInput value={i.discount} disabled={!rowEdit} onChange={(d) => setItem(i.key, { discount: d })} />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      disabled={!rowEdit}
                      data={VAT_RATES.map((r) => ({ value: String(r), label: `${r}%` }))}
                      value={String(i.vatRate)}
                      onChange={(r) => r && setItem(i.key, { vatRate: Number(r) })}
                      allowDeselect={false}
                    />
                  </Table.Td>
                  {seeCost && (
                    <Table.Td ta="right">
                      {!i.catalogItemId && rowEdit ? (
                        <MoneyInput
                          size="xs"
                          placeholder="brak"
                          aria-label={`Koszt zakupu: ${i.name}`}
                          data-cost-missing={i.purchasePriceGr === null ? v.key : undefined}
                          valueGr={i.purchasePriceGr}
                          onChangeGr={(gr) => setItem(i.key, { purchasePriceGr: gr })}
                        />
                      ) : i.purchasePriceGr !== null ? (
                        <Money gr={i.purchasePriceGr} currency={false} />
                      ) : (
                        <Tooltip label={i.catalogItemId ? 'Pozycja katalogu bez ceny zakupu — uzupełnij ją w katalogu i zaktualizuj ceny w ofercie' : 'Brak kosztu zakupu'} multiline w={260}>
                          <Text size="xs" c="orange">
                            brak{i.catalogItemId ? ' w cenniku' : ''}
                          </Text>
                        </Tooltip>
                      )}
                    </Table.Td>
                  )}
                  <Table.Td ta="right">
                    <Money gr={totals.items[idx]?.netGr} currency={false} />
                    {/* rabat na całość rozkłada się na pozycje — widać, skąd wartość niższa niż ilość × cena */}
                    {totals.items[idx] && totals.items[idx].netBeforeVariantGr !== totals.items[idx].netGr && (
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        przed rabatem na całość: <Money gr={totals.items[idx].netBeforeVariantGr} currency={false} />
                      </Text>
                    )}
                  </Table.Td>
                  {editable && (
                    <Table.Td>
                      {rowEdit && (
                        <ActionIcon variant="subtle" color="red" onClick={() => onChange({ ...v, items: v.items.filter((x) => x.key !== i.key) })} aria-label="Usuń pozycję">
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {canAdd && (
        <Group>
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={picker.open}>
            Dodaj z katalogu
          </Button>
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addCustom}>
            Dodaj pozycję własną
          </Button>
        </Group>
      )}
      <Group justify="space-between" align="flex-start" wrap="wrap">
        {seeMargin && serverTotals ? (
          <Paper withBorder p="xs" bg="var(--app-tint-yellow)" style={{ borderStyle: 'dashed' }}>
            <Text size="xs" fw={700} c="dimmed" tt="uppercase">
              Wewnętrzne — nie trafia do klienta
            </Text>
            <Text size="sm">
              Marża: <b>{serverTotals.marginPct === null ? 'brak danych o kosztach' : pctPl(serverTotals.marginPct)}</b>
              {serverTotals.missingCost > 0 && serverTotals.marginPct !== null && ` — niepełna: bez kosztu ${serverTotals.missingCost} poz., liczona tylko z pozycji z kosztem`}
            </Text>
            {serverTotals.costGr !== null && (
              <Text size="sm">
                Koszt: <Money gr={serverTotals.costGr} />
              </Text>
            )}
            <Text size="sm">Rabat względem cennika: {pctPl(totals.discountPct)}</Text>
            {editable && (
              <Text size="xs" c="dimmed">
                Marża przeliczana po zapisaniu.
              </Text>
            )}
          </Paper>
        ) : (
          <div />
        )}
        <Table w={{ base: '100%', sm: 360 }} withRowBorders={false}>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td>Razem netto</Table.Td>
              <Table.Td ta="right">
                <Money gr={totals.netGr} />
              </Table.Td>
            </Table.Tr>
            {totals.vatByRate.map((r) => (
              <Table.Tr key={r.rate}>
                <Table.Td c="dimmed">
                  VAT {r.rate}% od <Money gr={r.netGr} currency={false} />
                </Table.Td>
                <Table.Td ta="right">
                  <Money gr={r.vatGr} />
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-dark-4)' }}>
              <Table.Td fw={700}>Razem brutto</Table.Td>
              <Table.Td ta="right" fw={700} fz="lg">
                <Money gr={totals.grossGr} />
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Group>
      <CatalogPicker opened={pickerOpen} onClose={picker.close} onPick={add} tradeCodes={myScopes.map((s) => s.tradeCode)} onCustom={addCustom} />
    </Stack>
  );
}

/** Oferta wspólna: części (branże), kto przygotowuje, gotowość; „Moja część gotowa”. Wydanie — gdy wszystkie gotowe. */
function OfferParts({ v, onChanged }: { v: OfferVersionDto; onChanged: () => void }) {
  const mark = useMutation({
    mutationFn: (ready: boolean) => api<OfferVersionDto>(`/offer-versions/${v.id}/ready`, { method: 'POST', body: { ready } }),
    onSuccess: (_d, ready) => {
      notifyOk(ready ? 'Twoja część oznaczona jako gotowa' : 'Cofnięto gotowość');
      onChanged();
    },
    onError: notifyError,
  });
  const mine = v.parts.filter((p) => p.mine);
  const myReady = mine.length > 0 && mine.every((p) => p.readyAt);
  return (
    <Card withBorder>
      <Stack gap="xs">
        <Text fw={600} size="sm">
          Oferta wspólna — części
        </Text>
        <Text size="xs" c="dimmed">
          Każdy uzupełnia pozycje swojej branży; nagłówek i warianty zmienia autor oferty. Ofertę można wydać, gdy wszystkie części są gotowe.
        </Text>
        {v.parts.map((p) => (
          <Group key={p.scopeId} justify="space-between" wrap="nowrap" gap="xs">
            <div style={{ minWidth: 0 }}>
              <Text size="sm" fw={500}>
                {p.tradeName}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                {p.preparedByName || '—'}
                {p.mine ? ' (Ty)' : ''}
              </Text>
            </div>
            <Badge size="sm" variant="light" color={p.readyAt ? 'green' : 'gray'}>
              {p.readyAt ? 'gotowe' : 'w przygotowaniu'}
            </Badge>
          </Group>
        ))}
        {v.canMarkReady && v.status === 'roboczy' && (
          <Button size="xs" variant={myReady ? 'default' : 'light'} color={myReady ? 'gray' : 'green'} loading={mark.isPending} onClick={() => mark.mutate(!myReady)}>
            {myReady ? 'Cofnij gotowość mojej części' : 'Moja część gotowa'}
          </Button>
        )}
      </Stack>
    </Card>
  );
}

function CompareDialog({ a, b }: { a: OfferVersionDto; b: OfferVersionDto }) {
  const names = [...new Set([...a.variants.map((v) => v.name), ...b.variants.map((v) => v.name)])];
  return (
    <Stack>
      <Text size="sm">
        Porównanie wersji {a.versionNo} → {b.versionNo}
        {b.changeNote ? ` · „${b.changeNote}”` : ''}
      </Text>
      {names.map((n) => {
        const va = a.variants.find((v) => v.name === n);
        const vb = b.variants.find((v) => v.name === n);
        const items = [...new Set([...(va?.items ?? []).map((i) => i.name), ...(vb?.items ?? []).map((i) => i.name)])];
        return (
          <Card key={n} withBorder p="sm">
            <Group justify="space-between">
              <Text fw={700}>{n}</Text>
              <Text size="sm">
                <Money gr={va?.totals.grossGr} /> → <b><Money gr={vb?.totals.grossGr} /></b>
              </Text>
            </Group>
            <Table mt="xs">
              <Table.Tbody>
                {items.map((name) => {
                  const ia = va?.items.find((i) => i.name === name);
                  const ib = vb?.items.find((i) => i.name === name);
                  const changed = !ia || !ib || ia.quantity !== ib.quantity || ia.unitPriceGr !== ib.unitPriceGr || JSON.stringify(ia.discount) !== JSON.stringify(ib.discount);
                  if (!changed) return null;
                  return (
                    <Table.Tr key={name}>
                      <Table.Td>{name}</Table.Td>
                      <Table.Td>
                        {!ia ? (
                          <Badge color="green" size="xs">dodana</Badge>
                        ) : !ib ? (
                          <Badge color="red" size="xs">usunięta</Badge>
                        ) : (
                          <Text size="xs">
                            {ia.quantity !== ib.quantity && `ilość ${qtyPl(ia.quantity)} → ${qtyPl(ib.quantity)}; `}
                            {ia.unitPriceGr !== ib.unitPriceGr && (
                              <>
                                cena <Money gr={ia.unitPriceGr} /> → <Money gr={ib.unitPriceGr} />;{' '}
                              </>
                            )}
                            {JSON.stringify(ia.discount) !== JSON.stringify(ib.discount) && 'zmiana rabatu'}
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Card>
        );
      })}
    </Stack>
  );
}

export function OfferPage(_: { me: MeDto }) {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const offer = useQuery({ queryKey: ['offer', id], queryFn: () => api<OfferDto>(`/offers/${id}`) });
  const versionId = params.get('wersja') ?? offer.data?.versions[offer.data.versions.length - 1]?.id ?? null;
  const version = useQuery({ queryKey: ['offer-version', versionId], queryFn: () => api<OfferVersionDto>(`/offer-versions/${versionId}`), enabled: !!versionId });
  const caseQ = useCase(offer.data?.caseId ?? '');
  const trades = useTrades();
  const prevVersionId = useMemo(() => {
    const vs = offer.data?.versions ?? [];
    const idx = vs.findIndex((v) => v.id === versionId);
    return idx > 0 ? vs[idx - 1]!.id : null;
  }, [offer.data, versionId]);
  const prev = useQuery({ queryKey: ['offer-version', prevVersionId], queryFn: () => api<OfferVersionDto>(`/offer-versions/${prevVersionId}`), enabled: !!prevVersionId });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [baseline, setBaseline] = useState('');
  const [tab, setTab] = useState<string | null>('0');
  const [dialog, setDialog] = useState<null | 'issue' | 'request' | 'rejectRequest' | 'withdraw' | 'accept' | 'revoke' | 'compare' | 'share' | DecisionDialog>(null);
  const [reason, setReason] = useState('');
  /** uzasadnienie wyjątku (wydanie ponad próg) albo wiadomość do zatwierdzającego (prośba o wydanie) */
  const [exceptionNote, setExceptionNote] = useState('');
  const [acc, setAcc] = useState({ variantId: '', method: 'telefon', personName: '', note: '' });

  useEffect(() => {
    if (version.data) {
      const d = toDraft(version.data);
      setDraft(d);
      setBaseline(JSON.stringify(toPayload(d)));
    }
  }, [version.data]);

  const dirty = draft ? JSON.stringify(toPayload(draft)) !== baseline : false;
  const refresh = (v?: OfferVersionDto) => {
    if (v) qc.setQueryData(['offer-version', v.id], v);
    void qc.invalidateQueries({ queryKey: ['offer', id] });
    void qc.invalidateQueries({ queryKey: ['case-offers'] });
    if (offer.data) void qc.invalidateQueries({ queryKey: ['case', offer.data.caseId] });
  };
  const run = useMutation({
    mutationFn: async (fn: () => Promise<OfferVersionDto | void>) => fn(),
    onSuccess: (v) => {
      refresh(v ?? undefined);
      setDialog(null);
      setReason('');
      setExceptionNote('');
    },
    onError: notifyError,
  });
  const save = () => run.mutate(async () => {
    const v = await api<OfferVersionDto>(`/offer-versions/${versionId}`, { method: 'PUT', body: toPayload(draft!) });
    notifyOk('Zapisano wersję roboczą');
    // QA8 P1: np. koszt pozycji własnej pominięty — brak uprawnienia do cen zakupu
    if (v.saveWarnings?.length) notifyWarn(v.saveWarnings.join('\n'), 'Zapisano z ostrzeżeniem');
    return v;
  });
  useSaveShortcut(!!dirty && !run.isPending, save);
  const selectVersion = async (vid: string) => {
    if (dirty && !(await ask('Masz niezapisane zmiany w tej wersji.', { title: 'Porzucić zmiany?', confirm: 'Porzuć', danger: true }))) return;
    setParams({ wersja: vid });
    setTab('0');
  };

  const o = offer.data;
  const v = version.data;
  const editable = !!(o?.can.edit && v?.status === 'roboczy');
  // oferta wspólna: nagłówek i warianty — autor; pozycje — każdy swoje branże
  const headerEditable = editable && (v?.canEditHeader ?? true);
  const mineScopes = v?.shared ? v.editableScopeIds : null;
  const preparerOf = (scopeId: string) => v?.parts.find((p) => p.scopeId === scopeId)?.preparedByName ?? null;
  const waiting = v?.shared ? v.parts.filter((p) => !p.readyAt) : [];
  // progi przekroczone (Z-14), a użytkownik nie ma prawa wydania wyjątku — zamiast „Wydaj” prośba do zatwierdzającego
  const needsException = !!(v?.thresholdExceeded && o && !o.can.issueBelowThreshold);
  const scopes = (caseQ.data?.scopes ?? []).filter((s) => o?.scopeIds.includes(s.id));
  const tradeName = (code: string) => trades.data?.find((t) => t.code === code)?.name ?? code;
  // QA P6: 8% przy zakresie usługowym (audyt, świadectwo…) — ostrzeżenie bez blokady (docs/09 §3.5)
  // QA8 P1: skrót z okna wydania (wyjątku) — zamyka okno, przechodzi do wariantu z brakującym kosztem pozycji własnej i ustawia kursor w polu
  const fillCosts = () => {
    if (!draft || !v) return;
    setDialog(null);
    const custom = draft.variants.findIndex((va) => va.items.some((i) => !i.catalogItemId && i.purchasePriceGr === null && (!mineScopes || mineScopes.includes(i.scopeId))));
    const idx = custom >= 0 ? custom : Math.max(0, v.variants.findIndex((x) => x.totals.missingCost > 0));
    setTab(String(idx));
    if (custom < 0) notifyWarn('Koszt brakuje tylko w pozycjach z katalogu — uzupełnij cenę zakupu w katalogu, a potem „Zaktualizuj ceny” w wariancie.', 'Uzupełnij koszty');
    const key = draft.variants[idx]?.key;
    window.setTimeout(() => document.querySelector<HTMLInputElement>(`[data-cost-missing="${key}"]`)?.focus(), 100);
  };
  const vatWarning = draft ? serviceVatWarning(scopes.filter((s) => trades.data?.find((t) => t.code === s.tradeCode)?.kind === 'usluga').map((s) => tradeName(s.tradeCode)), draft.vatProfile === '8' || draft.variants.some((va) => va.items.some((i) => i.vatRate === 8))) : null;

  return (
    <QueryState isLoading={offer.isLoading || version.isLoading} error={offer.error ?? version.error}>
      {o && v && draft && (
        <div className={editable ? undefined : "doc-readonly"}>
          <PageHeader
            crumbs={[
              { label: 'Sprawy', to: '/sprawy' },
              { label: o.caseNumber, to: `/sprawy/${o.caseId}?zakladka=oferty` },
              { label: `Oferta${v.versionNo > 1 ? ` · wersja ${v.versionNo}` : ''}` },
            ]}
            title={v.issuedNumber ?? (o.number ? `${o.number} — wersja ${v.versionNo}` : 'Oferta (numer przy wydaniu)')}
            badge={
              <Group gap={6} wrap="wrap">
                <VersionStatusBadge status={v.effectiveStatus} caseClosed={o.caseClosed} />
                <CompanyBadgeById id={o.companyId} full />
                {scopes.map((s) => (
                  <TradeBadge key={s.id} code={s.tradeCode} />
                ))}
              </Group>
            }
            description={`${o.clientName} · ${o.siteAddress}`}
            actions={
            <>
              <UnsavedHint show={dirty} action="zobaczyć PDF lub wydać ofertę" />
              <Button
                variant="default"
                leftSection={<IconFileTypePdf size={16} />}
                component="a"
                href={dirty ? undefined : `/api/offer-versions/${v.id}/pdf`}
                target="_blank"
                disabled={dirty}
                title={dirty ? 'Zapisz zmiany, aby zobaczyć PDF' : undefined}
              >
                {v.status === 'roboczy' ? 'Podgląd PDF' : 'PDF'}
              </Button>
              {editable && (
                <>
                  <Button variant="light" onClick={save} loading={run.isPending} disabled={!dirty}>
                    Zapisz
                  </Button>
                  {o.can.issue && (
                    <Tooltip label={`Czekamy na gotowość: ${waiting.map((p) => `${p.tradeName} (${p.preparedByName})`).join(', ')}`} disabled={!waiting.length} multiline w={300}>
                      {needsException ? (
                        <Button onClick={() => setDialog('request')} disabled={dirty || waiting.length > 0} variant={v.issueRequest ? 'light' : 'filled'}>
                          {v.issueRequest ? 'Ponów prośbę o wydanie' : 'Poproś o wydanie (wyjątek)'}
                        </Button>
                      ) : (
                        <Button onClick={() => setDialog('issue')} disabled={dirty || waiting.length > 0}>
                          Wydaj
                        </Button>
                      )}
                    </Tooltip>
                  )}
                </>
              )}
              {v.canRejectRequest && (
                <Button color="red" variant="light" onClick={() => setDialog('rejectRequest')} disabled={dirty}>
                  Odrzuć prośbę
                </Button>
              )}
              {v.status !== 'roboczy' && o.can.share && ['wydany', 'zaakceptowany'].includes(v.effectiveStatus) && (
                <Button leftSection={<IconSend size={16} />} onClick={() => setDialog('share')}>
                  Udostępnij klientowi
                </Button>
              )}
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon variant="default" size="lg" aria-label="Więcej">
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {o.can.edit && !o.versions.some((x) => x.status === 'roboczy') && v.status !== 'roboczy' && (
                    <Menu.Item
                      leftSection={<IconCopy size={14} />}
                      onClick={() =>
                        run.mutate(async () => {
                          const nv = await api<OfferVersionDto>(`/offers/${o.id}/versions`, { method: 'POST', body: { fromVersionId: v.id } });
                          setParams({ wersja: nv.id });
                          return nv;
                        })
                      }
                    >
                      Nowa wersja (negocjacje / przedłużenie)
                    </Menu.Item>
                  )}
                  {prevVersionId && <Menu.Item onClick={() => setDialog('compare')}>Porównaj z poprzednią wersją</Menu.Item>}
                  {o.can.recordAcceptance && v.effectiveStatus === 'wydany' && (
                    <Menu.Item leftSection={<IconCheck size={14} />} onClick={() => setDialog('accept')}>
                      Odnotuj akceptację (telefon, papier, e-mail)
                    </Menu.Item>
                  )}
                  {o.can.recordAcceptance && v.status !== 'roboczy' && ['wydany', 'zaakceptowany', 'do_zmiany'].includes(v.effectiveStatus) && (
                    <Menu.Item onClick={() => setDialog('delivery')}>Odnotuj przekazanie klientowi</Menu.Item>
                  )}
                  {o.can.recordAcceptance && v.effectiveStatus === 'wydany' && <Menu.Item onClick={() => setDialog('changes')}>Klient prosi o zmiany</Menu.Item>}
                  {o.can.recordAcceptance && ['wydany', 'do_zmiany'].includes(v.effectiveStatus) && (
                    <Menu.Item color="orange" onClick={() => setDialog('reject')}>
                      Klient odrzucił ofertę
                    </Menu.Item>
                  )}
                  {o.can.withdraw && v.status === 'zaakceptowany' && (
                    <Menu.Item color="orange" onClick={() => setDialog('revoke')}>
                      Wycofaj akceptację
                    </Menu.Item>
                  )}
                  {o.can.withdraw && ['wydany', 'wygasly'].includes(v.status) && (
                    <Menu.Item color="red" onClick={() => setDialog('withdraw')}>
                      Wycofaj ofertę
                    </Menu.Item>
                  )}
                  {editable && (
                    <Menu.Item
                      color="red"
                      leftSection={<IconTrash size={14} />}
                      onClick={async () => {
                        if (!(await ask('Wersji roboczej nie da się przywrócić.', { title: 'Usunąć wersję roboczą?', confirm: 'Usuń', danger: true }))) return;
                        run.mutate(async () => {
                          const r = await api<{ offerDeleted: boolean }>(`/offer-versions/${v.id}`, { method: 'DELETE' });
                          if (r.offerDeleted) navigate(`/sprawy/${o.caseId}?zakladka=oferty`);
                          else setParams({});
                        });
                      }}
                    >
                      Usuń wersję roboczą
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            </>
            }
          />

          {v.acceptance && !v.acceptance.revokedAt && (
            <Alert color="green" icon={<IconCheck />} mb="md" title={`Zaakceptowano wariant „${v.acceptance.variantName}” — ${ACCEPTANCE_METHODS[v.acceptance.method]}`}>
              {v.acceptance.personName}
              {v.acceptance.email ? ` (${v.acceptance.email})` : ''}, {formatDateTime(v.acceptance.acceptedAt)} · kwota <Money gr={v.acceptance.totalGrossGr} /> brutto
              {v.acceptance.recordedByName ? ` · odnotował(a) ${v.acceptance.recordedByName}` : ''}
              <Text size="xs" c="dimmed" ff="monospace">
                Skrót treści (SHA-256): {v.acceptance.contentHash}
              </Text>
              <Text size="xs" mt={4}>
                Akceptacja nie jest umową — umowę przygotujesz w sprawie, w zakładce „Umowy”.
              </Text>
            </Alert>
          )}
          <OfferDecisionInfo v={v} />
          <OfferDecisionModals v={v} dialog={dialog === 'delivery' || dialog === 'reject' || dialog === 'changes' ? dialog : null} onClose={() => setDialog(null)} run={(fn) => run.mutate(fn)} busy={run.isPending} />
          {v.rejection && (
            <Alert color="orange" mb="md" title={`Klient odrzucił ofertę — ${v.rejection.reasonLabel}`}>
              {v.rejection.personName}, {formatDateTime(v.rejection.rejectedAt)}
              {v.rejection.method !== 'link' ? ` · odnotowane przez biuro${v.rejection.recordedByName ? ` (${v.rejection.recordedByName})` : ''}` : ' · na stronie z linkiem'}
              {v.rejection.reason ? ` · „${v.rejection.reason}”` : ''}
              <Text size="xs" mt={4}>
                Tej wersji nie można już zaakceptować. Przygotuj nową wersję (menu ⋯ → Nowa wersja) albo oznacz zakres jako przegrany.
              </Text>
            </Alert>
          )}
          {v.status === 'wycofany' && (
            <Alert color="red" mb="md">
              Wersja wycofana: {v.withdrawnReason}
            </Alert>
          )}
          {v.status !== 'roboczy' && (
            <Alert color="gray" variant="light" mb="md">
              Wydana {formatDateTime(v.issuedAt)} przez {v.issuedByName}. Treść jest niezmienna — zmiany tylko w nowej wersji. Skrót treści (SHA-256):{' '}
              <Text span ff="monospace" size="xs">
                {v.contentHash}
              </Text>
            </Alert>
          )}
          {v.thresholdExceeded && (
            <Alert color="orange" icon={<IconAlertTriangle />} mb="md" title="Przekroczone progi (wewnętrzne)">
              {v.thresholdIssues.length > 0 ? v.thresholdIssues.join(' · ') : 'Rabat lub marża tej wersji przekracza progi firmy.'}
              {editable && !o.can.issueBelowThreshold && (
                <Text size="sm">
                  Wydać ofertę może osoba z uprawnieniem do zatwierdzania wyjątków (właściciel)
                  {o.can.issue ? ' — użyj „Poproś o wydanie (wyjątek)”, a zatwierdzający dostanie powiadomienie z linkiem do oferty.' : '.'}
                </Text>
              )}
              <IssueRequestInfo v={v} />
            </Alert>
          )}

          <Grid gap="lg">
            <Grid.Col span={{ base: 12, xl: 9 }}>
              <Tabs value={tab} onChange={setTab}>
                <Tabs.List mb="md">
                  {draft.variants.map((va, i) => (
                    <Tabs.Tab key={va.key} value={String(i)} rightSection={v.acceptance && !v.acceptance.revokedAt && v.variants[i]?.id === v.acceptance.variantId ? <IconCheck size={14} color="green" /> : undefined}>
                      {va.name || `Wariant ${i + 1}`} · <Money gr={totalsOf(va).grossGr} />
                    </Tabs.Tab>
                  ))}
                  {headerEditable && draft.variants.length < 6 && (
                    <Menu withinPortal>
                      <Menu.Target>
                        <Button size="compact-sm" variant="subtle" leftSection={<IconPlus size={14} />} ml="xs">
                          Dodaj wariant
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          onClick={() => {
                            setDraft({ ...draft, variants: [...draft.variants, { key: k(), name: `Wariant ${String.fromCharCode(65 + draft.variants.length)}`, description: '', presentation: 'szczegolowa', discount: null, items: [] }] });
                            setTab(String(draft.variants.length));
                          }}
                        >
                          Pusty wariant
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => {
                            const src = draft.variants[Number(tab)] ?? draft.variants[0]!;
                            setDraft({
                              ...draft,
                              variants: [...draft.variants, { ...src, key: k(), name: `${src.name} (kopia)`, items: src.items.map((i) => ({ ...i, key: k(), id: null })) }],
                            });
                            setTab(String(draft.variants.length));
                          }}
                        >
                          Kopia bieżącego wariantu
                        </Menu.Item>
                        {draft.variants.length > 1 && (
                          <Menu.Item
                            color="red"
                            onClick={async () => {
                              const idx = Number(tab);
                              const name = draft.variants[idx]?.name || `Wariant ${idx + 1}`;
                              if (!(await ask(`Wariant „${name}” zniknie z tej wersji roboczej razem z pozycjami (do czasu zapisu można porzucić zmiany).`, { title: 'Usunąć bieżący wariant?', confirm: 'Usuń', danger: true }))) return;
                              setDraft({ ...draft, variants: draft.variants.filter((_, i) => i !== idx) });
                              setTab('0');
                            }}
                          >
                            Usuń bieżący wariant
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Tabs.List>
                {draft.variants.map((va, i) => (
                  <Tabs.Panel key={va.key} value={String(i)}>
                    <VariantEditor
                      v={va}
                      editable={editable}
                      mineScopes={mineScopes}
                      headerEditable={headerEditable}
                      preparerOf={preparerOf}
                      profileRate={draft.vatProfile === '8' ? 8 : draft.vatProfile === '23' ? 23 : null}
                      scopes={scopes}
                      tradeName={tradeName}
                      seeMargin={o.can.seeMargin}
                      seeCost={o.can.seeCost}
                      serverTotals={dirty ? undefined : v.variants[i]?.totals}
                      onChange={(nv) => setDraft({ ...draft, variants: draft.variants.map((x, j) => (j === i ? nv : x)) })}
                    />
                  </Tabs.Panel>
                ))}
              </Tabs>
            </Grid.Col>
            <Grid.Col span={{ base: 12, xl: 3 }}>
              <Stack>
                {v.shared && <OfferParts v={v} onChanged={() => void version.refetch()} />}
                <Card withBorder>
                  <Stack gap="xs">
                    <TextInput label="Tytuł oferty" value={draft.title} disabled={!headerEditable} onChange={(e) => setDraft({ ...draft, title: e.currentTarget.value })} />
                    <TextInput label="Ważna do" type="date" value={draft.validUntil} disabled={!headerEditable} onChange={(e) => setDraft({ ...draft, validUntil: e.currentTarget.value })} />
                    <Select
                      label="Profil VAT"
                      description="Ustala domyślne stawki — stawkę potwierdza osoba przygotowująca ofertę."
                      data={Object.entries(VAT_PROFILES).map(([value, label]) => ({ value, label }))}
                      value={draft.vatProfile}
                      disabled={!headerEditable}
                      allowDeselect={false}
                      onChange={(p) => {
                        if (!p) return;
                        const rate = p === '8' ? 8 : p === '23' ? 23 : null;
                        setDraft({
                          ...draft,
                          vatProfile: p as VatProfile,
                          variants: rate === null ? draft.variants : draft.variants.map((va) => ({ ...va, items: va.items.map((i) => ({ ...i, vatRate: rate })) })),
                        });
                      }}
                    />
                    {vatWarning && v.status === 'roboczy' && (
                      <Alert color="yellow" variant="light" icon={<IconAlertTriangle />} p="xs">
                        <Text size="xs">{vatWarning}</Text>
                      </Alert>
                    )}
                    <Textarea label="Warunki (widoczne w ofercie)" autosize minRows={3} value={draft.terms} disabled={!headerEditable} onChange={(e) => setDraft({ ...draft, terms: e.currentTarget.value })} />
                    {v.versionNo > 1 && (
                      <Textarea label="Co zmieniono i dlaczego" description="Historia negocjacji" autosize minRows={2} value={draft.changeNote} disabled={!editable} onChange={(e) => setDraft({ ...draft, changeNote: e.currentTarget.value })} />
                    )}
                  </Stack>
                </Card>
                <ContractTermsCard o={o} />
                <SectionCard title="Wersje">
                  <SegmentedControl
                    orientation="vertical"
                    fullWidth
                    value={v.id}
                    onChange={selectVersion}
                    data={o.versions.map((x) => ({
                      value: x.id,
                      label: (
                        <Group gap={6} justify="space-between" wrap="nowrap">
                          <Text size="sm">v{x.versionNo}</Text>
                          <VersionStatusBadge status={x.effectiveStatus} size="xs" />
                        </Group>
                      ),
                    }))}
                  />
                  {o.versions.find((x) => x.id === v.id)?.changeNote && (
                    <Text size="xs" c="dimmed" mt="xs">
                      „{o.versions.find((x) => x.id === v.id)?.changeNote}”
                    </Text>
                  )}
                </SectionCard>
              </Stack>
            </Grid.Col>
          </Grid>

          <Modal opened={dialog === 'issue'} onClose={() => setDialog(null)} title="Wydanie oferty">
            <Stack>
              <Text size="sm">
                Po wydaniu treść tej wersji zostanie zamrożona (numer, PDF, skrót treści). Zmiany będą możliwe tylko w nowej wersji.
                {v.versionNo > 1 && ' Poprzednia wydana wersja zostanie oznaczona jako zastąpiona — klient nie zaakceptuje jej już z linku.'}
              </Text>
              {v.issueWarnings.length > 0 && (
                <Alert color="yellow" icon={<IconAlertTriangle />} title="Uwaga — dane firmy">
                  <IssueWarnings items={v.issueWarnings} />
                </Alert>
              )}
              {vatWarning && (
                <Alert color="yellow" icon={<IconAlertTriangle />} title="Uwaga — stawka VAT">
                  {vatWarning}
                </Alert>
              )}
              {/* QA8 P3: zawsze (nie tylko przy wyjątku) — marża liczona tylko z pozycji z kosztem; P1: skrót do uzupełnienia kosztów */}
              {v.costMissing > 0 && (
                <Alert color="yellow" icon={<IconAlertTriangle />} title="Uwaga — pozycje bez kosztu">
                  <Text size="sm">{marginIncompleteNote(v.costMissing)}.</Text>
                  {editable && o.can.seeCost && (
                    <Button size="xs" variant="light" color="yellow" mt="xs" onClick={fillCosts}>
                      Uzupełnij koszty
                    </Button>
                  )}
                </Alert>
              )}
              {v.thresholdExceeded && (
                <>
                  <Alert color="orange" title="Wydanie z wyjątkiem (progi rabatu / marży)">
                    {v.thresholdIssues.length > 0 ? v.thresholdIssues.join(' · ') : 'Rabat lub marża przekracza progi firmy.'}
                    <IssueRequestInfo v={v} />
                  </Alert>
                  <Textarea
                    label="Uzasadnienie wyjątku"
                    description={minHint(exceptionNote)}
                    required
                    autosize
                    minRows={2}
                    maxLength={1000}
                    value={exceptionNote}
                    onChange={(e) => setExceptionNote(e.currentTarget.value)}
                    data-autofocus
                  />
                </>
              )}
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Anuluj
                </Button>
                <Button
                  loading={run.isPending}
                  disabled={v.thresholdExceeded && exceptionNote.trim().length < 3}
                  onClick={() =>
                    run.mutate(async () => {
                      const r = await api<OfferVersionDto>(`/offer-versions/${v.id}/issue`, { method: 'POST', body: { exceptionReason: v.thresholdExceeded ? exceptionNote.trim() : '' } });
                      notifyOk(`Wydano ofertę ${r.issuedNumber}`);
                      if (r.issueWarnings.length) notifyWarn(r.issueWarnings.join('\n'), 'Oferta wydana z brakami w danych firmy');
                      return r;
                    })
                  }
                >
                  {v.thresholdExceeded ? 'Wydaj z wyjątkiem' : 'Wydaj ofertę'}
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal opened={dialog === 'request'} onClose={() => setDialog(null)} title="Prośba o wydanie oferty (wyjątek)">
            <Stack>
              <Text size="sm">
                Oferta przekracza progi rabatu lub marży, więc wydać ją może tylko osoba z uprawnieniem do zatwierdzania wyjątków. Powstanie zadanie „Zatwierdź wyjątek i wydaj ofertę” z terminem 1 dnia roboczego
                (skrzynka zadań, dzwonek, e-mail — według ustawień powiadomień), a prośba zostanie zapisana w historii sprawy. Zatwierdzający wyda ofertę albo odrzuci prośbę z komentarzem. Gdy ktoś wyda ofertę, dostaniesz powiadomienie, że można ją wysłać klientowi.
              </Text>
              {v.thresholdIssues.length > 0 && <Alert color="orange">{v.thresholdIssues.join(' · ')}</Alert>}
              {v.costMissing > 0 && (
                <Alert color="yellow" icon={<IconAlertTriangle />}>
                  {missingCostNote(v.costMissing)}. Zatwierdzający zobaczy tę informację w zadaniu i w e-mailu.
                </Alert>
              )}
              <IssueRequestInfo v={v} dimmed />
              <Textarea
                label="Wiadomość dla zatwierdzającego"
                description="Np. dlaczego rabat jest potrzebny (opcjonalnie)"
                autosize
                minRows={2}
                maxLength={1000}
                value={exceptionNote}
                onChange={(e) => setExceptionNote(e.currentTarget.value)}
                data-autofocus
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Anuluj
                </Button>
                <Button
                  loading={run.isPending}
                  onClick={() =>
                    run.mutate(async () => {
                      const r = await api<OfferVersionDto>(`/offer-versions/${v.id}/issue-request`, { method: 'POST', body: { note: exceptionNote.trim() } });
                      notifyOk('Wysłano prośbę o wydanie oferty');
                      return r;
                    })
                  }
                >
                  Wyślij prośbę
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal opened={dialog === 'rejectRequest'} onClose={() => setDialog(null)} title="Odrzucenie prośby o wydanie (wyjątek)">
            <Stack>
              <Text size="sm">Oferta zostaje wersją roboczą. Handlowiec dostanie powiadomienie z Twoim komentarzem, a zadanie zatwierdzenia zamknie się samo.</Text>
              <IssueRequestInfo v={v} dimmed />
              <Textarea
                label="Komentarz dla handlowca"
                description={minHint(reason)}
                placeholder="np. maksymalnie 15% — zmniejsz rabat w wariancie A"
                required
                autosize
                minRows={2}
                maxLength={1000}
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                data-autofocus
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Anuluj
                </Button>
                <Button
                  color="red"
                  disabled={reason.trim().length < 3}
                  loading={run.isPending}
                  onClick={() =>
                    run.mutate(async () => {
                      const r = await api<OfferVersionDto>(`/offer-versions/${v.id}/issue-request/reject`, { method: 'POST', body: { comment: reason.trim() } });
                      notifyOk('Odrzucono prośbę — handlowiec dostanie powiadomienie');
                      return r;
                    })
                  }
                >
                  Odrzuć prośbę
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal opened={dialog === 'withdraw' || dialog === 'revoke'} onClose={() => setDialog(null)} title={dialog === 'withdraw' ? 'Wycofanie oferty' : 'Wycofanie akceptacji'}>
            <Stack>
              <Text size="sm">
                {dialog === 'withdraw'
                  ? 'Wersja zostanie oznaczona jako wycofana; klient nie zaakceptuje jej z linku. Wpis pozostaje w historii.'
                  : 'Akceptacja pozostanie w historii z adnotacją o wycofaniu. Zakres wróci do negocjacji.'}
              </Text>
              <TextInput label="Powód" required description={minHint(reason)} value={reason} onChange={(e) => setReason(e.currentTarget.value)} data-autofocus />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Anuluj
                </Button>
                <Button
                  color={dialog === 'withdraw' ? 'red' : 'orange'}
                  disabled={reason.trim().length < 3}
                  loading={run.isPending}
                  onClick={() =>
                    run.mutate(() => api<OfferVersionDto>(`/offer-versions/${v.id}/${dialog === 'withdraw' ? 'withdraw' : 'revoke-acceptance'}`, { method: 'POST', body: { reason } }))
                  }
                >
                  {dialog === 'withdraw' ? 'Wycofaj ofertę' : 'Wycofaj akceptację'}
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal opened={dialog === 'accept'} onClose={() => setDialog(null)} title="Odnotowanie akceptacji klienta">
            <Stack>
              <Select label="Wariant" data={v.variants.map((x) => ({ value: x.id, label: x.name }))} value={acc.variantId || null} onChange={(x) => setAcc({ ...acc, variantId: x ?? '' })} />
              <Select
                label="Sposób"
                data={Object.entries(ACCEPTANCE_METHODS)
                  .filter(([value]) => value !== 'link' && value !== 'ekran')
                  .map(([value, label]) => ({ value, label }))}
                value={acc.method}
                onChange={(x) => x && setAcc({ ...acc, method: x })}
                allowDeselect={false}
              />
              <TextInput label="Kto zaakceptował" required value={acc.personName} onChange={(e) => setAcc({ ...acc, personName: e.currentTarget.value })} />
              <Textarea label="Uwagi" value={acc.note} onChange={(e) => setAcc({ ...acc, note: e.currentTarget.value })} />
              <Text size="xs" c="dimmed">
                Akceptacja nie jest umową i nie zezwala na rozpoczęcie prac.
              </Text>
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Anuluj
                </Button>
                <Button
                  disabled={!acc.variantId || acc.personName.trim().length < 3}
                  loading={run.isPending}
                  onClick={() => run.mutate(() => api<OfferVersionDto>('/offer-acceptances', { method: 'POST', body: { versionId: v.id, ...acc } }))}
                >
                  Zapisz akceptację
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal opened={dialog === 'compare'} onClose={() => setDialog(null)} title="Porównanie wersji" size="lg">
            {prev.data && <CompareDialog a={prev.data} b={v} />}
          </Modal>

          {caseQ.data && (
            <ShareDialog
              opened={dialog === 'share'}
              onClose={() => setDialog(null)}
              c={caseQ.data}
              preselect={[v.id]}
              defaultEmail={o.clientEmail}
            />
          )}

        </div>
      )}
    </QueryState>
  );
}
